// Approval queue — everything waiting on a hardware signature.
//
// This screen LISTS and HANDS OFF — to the Ledger. Two ways to it (17 Oct
// 2026, the operator: "terminal Signer I would like to have it in my dashboard
// itself"): where the browser reaches a Ledger over a cable (WebHID: Brave or
// Edge on a computer), "Sign on Ledger" opens the cable sheet, which decodes
// and checks the card with the signing page's own functions and sends the
// device exactly the bytes it showed; everywhere else, and as the other path
// where it does, "Review on a phone" hands a signing link to the device that
// holds TronLink. Neither signs in this bundle's own code: the device signs,
// the server takes only the registered device's signature, and the sheet's
// checks are the page's, imported, not copied.
//
// The party NAME leads and the address is secondary. That ordering is a
// deliberate defence: address-poisoning works precisely because operators
// compare the first and last few characters of a base58 string. An entry that
// is NOT in the whitelist is called out loudly rather than rendered as a
// slightly different address.
//
// On the design system (22 Oct 2026): the figure is the one figure of the
// card; every warning is one line, its reason behind Details.
//
// Screens' round 2 (23 Oct 2026): ONE decision gates every entry point of a
// card — cable, phone and the second device's approval (lib/custody-screen.ts
// cardActions); the approval mutation lives on the screen, not on the card,
// and the list stays mounted through a failed poll (listState), so nothing in
// flight is unmounted by a 3-second refresh.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { custody as api, type ApprovalRow } from '../../lib/api';
import { money, shortAddress, timeLeft, POOL_WORD, KIND_WORD } from '../../lib/custody-format';
import { Card, Label, StateLine, EmptyState, Pending, Pill, MoreDetails } from '../../components/kit';
import { ExternalLink, Clock, Usb } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { CosignHandoff } from '../../components/CosignHandoff';
import { webHid, signMessageOnLedger, accountPath, buildNetwork } from '../../lib/ledger-cable';
import { buildCardApprovalMessage } from '../../../../signer/src/card-approval.mjs';
import { fmtDate } from '../../lib/format';
import { CableSign } from './CableSign';
import { approvalOutcome, approvalUnknownDetail } from './refusals';
import { ReadFailed } from './ReadFailed';
import { cardActions, cableSigns, listState, approvalReducer, approvalDeadline, APPROVAL_IDLE, READ_UNTIMED, type ApprovalEnd, type ApprovalStage, readStartedAt, type ListRefetch } from '../../lib/custody-screen';
import { ApiError } from '../../lib/errors';

const KIND_LABEL: Record<ApprovalRow['kind'], string> = KIND_WORD as Record<ApprovalRow['kind'], string>;
/** A card's kind in fixed words: one this screen does not know is "Card to sign", never the server's own text (Astra,
 *  max, pre-live, 29 Oct 2026: a crafted kind beside the "type the ending" box could hand the operator the answer). */
export const kindLabel = (kind: string): string => (KIND_LABEL as Record<string, string>)[kind] ?? 'Card to sign';

/**
 * The second device's approval of a payout card (N-068), held by the SCREEN:
 * the registered device signs the approval message naming the card, by
 * cable; the server takes it from a device that is neither the card's signer
 * nor its proposer. One device operation at a time (`busy`); how each card's
 * latest approval ended is kept BY CARD (lib/custody-screen.ts
 * approvalReducer) and stays until an authoritative read of the list
 * supersedes it — starting card B does not forget card A's refusal, and a
 * failed refresh does not forget a success (Screens' round 3). An answer
 * lost AFTER the device signed and the submission began is not a failure
 * but an unknown: the list, read again, says whether it was recorded
 * (Screens' round 4).
 */
export type CardApproval = {
  start: (a: ApprovalRow) => void;
  /** Read the list again, now — the one thing that settles an unknown outcome. */
  check: () => void;
  /** The card an approval is running for; null when idle. One at a time. */
  busy: string | null;
  /** The device's own step while busy, as ledger-cable words it. */
  step: string | null;
  /** How each card's latest approval ended, by txid. */
  ended: Readonly<Record<string, ApprovalEnd>>;
};

/** An error the screen itself raises before the device is asked: its own words, the way out, and whether pressing again can help. */
const localRefusal = (message: string, remedy: string, retry = true): Error => Object.assign(new Error(message), { remedy, retry });

/**
 * @param rows    the list as last read successfully: what supersedes a kept outcome.
 * @param refetch the list's own refetch: a read the hook starts itself is stamped with the time its REQUEST started — the only
 *                read that may settle an unknown outcome (Screens' round 6: a poll's answer arriving late proves nothing).
 */
export function useCardApproval(rows: ReadonlyArray<ApprovalRow> | undefined, refetch: ListRefetch): CardApproval {
  const qc = useQueryClient();
  const network = useMemo(() => buildNetwork(), []);
  const [state, dispatch] = useReducer(approvalReducer, APPROVAL_IDLE);
  // How far the running attempt got, for what its failure means.
  const stage = useRef<ApprovalStage>('before');
  const m = useMutation({
    mutationFn: async (a: ApprovalRow) => {
      stage.current = 'before';
      const hid = webHid();
      if (!hid) throw localRefusal('This browser cannot reach a Ledger over a cable.', 'Use Brave or Edge on the computer the Ledger is plugged into.');
      if (!network) throw localRefusal('This build of the app names no TRON network.', 'The app was set up wrongly: report this. Approving again will not help.', false);
      const message = buildCardApprovalMessage({ txid: a.txid, at: Date.now() });
      const signed = await signMessageOnLedger({ hid, network, path: accountPath(0), message, onStep: (_s, text) => dispatch({ type: 'step', text }) });
      stage.current = 'signed';
      stage.current = 'submitted';
      return api.approveCard(a.txid, { message, signature: signed.signature });
    },
    onMutate: (a) => dispatch({ type: 'start', txid: a.txid }),
    onSuccess: (_answer, a) => {
      dispatch({ type: 'done', txid: a.txid });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
    },
    onError: (error, a) => {
      dispatch({ type: 'failed', txid: a.txid, error, stage: stage.current, at: Date.now() });
      // "Already approved" is the other device's word arriving first, and an
      // unknown outcome is settled only by the list: read it again, now.
      const reason = error instanceof ApiError && typeof error.body.reason === 'string' ? error.body.reason : null;
      if (reason === 'already_approved' || (reason === null && stage.current === 'submitted')) void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
    },
  });
  // Every successful read supersedes an outcome whose card is gone or
  // approved. A poll's request time is not known, so it settles no unknown.
  useEffect(() => {
    if (rows) dispatch({ type: 'read', rows, at: READ_UNTIMED });
  }, [rows]);
  // A read the hook starts itself: the time its REQUEST started is stamped
  // on the result, so only a read begun after the attempt's deadline can say
  // "not recorded". refetch cancels any poll in flight and starts a new
  // request, so the answer is this request's.
  // A failed read dispatches nothing — the list's own error line says it, the
  // outcome stays as it is (readStartedAt: React Query resolves a failed
  // refetch WITH the cached rows unless asked to throw; Screens' round 7).
  const check = useCallback(async () => {
    const action = await readStartedAt(refetch, Date.now());
    if (action) dispatch(action);
  }, [refetch]);
  // When an attempt's window elapses, one read is started for it, then.
  const deadline = approvalDeadline(state.ended);
  useEffect(() => {
    if (deadline === null) return;
    const wait = Math.max(0, deadline - Date.now() + 1_000);
    const timer = setTimeout(() => { void check(); }, wait);
    return () => clearTimeout(timer);
  }, [deadline, check]);
  return {
    start: (a) => m.mutate(a),
    check: () => { void check(); },
    busy: state.busy, step: state.step, ended: state.ended,
  };
}

export function ApprovalCard({ a, onReview, onCable, approval }: {
  a: ApprovalRow;
  onReview: (a: ApprovalRow) => void;
  /** Set where the browser reaches a Ledger over a cable. */
  onCable: ((a: ApprovalRow) => void) | null;
  /** The screen's approval, where the browser reaches a Ledger over a cable; null elsewhere. */
  approval: CardApproval | null;
}) {
  const left = timeLeft(a.expiresAt);
  const unknownPayee = a.kind === 'payout' && !a.partyName;
  // What the card may offer (lib/custody-screen.ts): a payout nobody can name
  // offers no entry point beside "do not sign" — not the approval either.
  const offers = cardActions(a, left.expired);
  // A row missing its recipient or amount is not a card to tidy up — it is a
  // stored transaction we cannot describe, and offering a signing button for it
  // would send an operator to their device with nothing to check against.
  const { toAddress, amount } = a;

  if (toAddress === null || amount === null) {
    return (
      <Card tone="bad">
        <Label>{kindLabel(a.kind)}</Label>
        <StateLine tone="bad" className="mt-2" more="The recipient or the amount is missing from the stored transaction, so nothing here can be checked against the device.">
          Incomplete card — do not sign; report it.
        </StateLine>
        <p className="mt-2 font-mono text-meta text-tg-hint">{a.txid.slice(0, 16)}…</p>
      </Card>
    );
  }

  return (
    <Card tone={unknownPayee ? 'bad' : undefined} className={left.expired ? 'opacity-60' : ''}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Label>{kindLabel(a.kind)}</Label>
          {/* Amount is the thing an operator checks first, so it leads. */}
          <div className="font-mono text-figure font-semibold tabular">
            {money(amount, a.asset)}
          </div>
        </div>
        <Pill tone={left.expired ? 'neutral' : left.urgent ? 'bad' : 'neutral'} icon={<Clock size={11} />}>{left.text}</Pill>
      </div>

      <div className="mt-3 space-y-1">
        <div className="row">
          <span className="label">To</span>
          <span className="text-right text-body">
            {a.partyName
              ? <span className="font-medium">{a.partyName}</span>
              : a.kind === 'payout'
                ? <span className="font-medium text-neg">Not an approved payout address</span>
                : <span className="font-medium text-tg-hint">Not named by the desk</span>}
          </span>
        </div>
        <div className="row">
          <span className="label">Address</span>
          <span className="font-mono text-body text-tg-hint">
            {/* The head only: the ending is what the sheet may ask him to type (audit of 21 Oct, E3). */}
            {shortAddress(toAddress, 10, 0)}
          </span>
        </div>
        {a.pool && a.pool !== 'clean' && (
          <div className="row">
            <span className="label">Wallet</span>
            <span className="text-body font-medium text-neg">{POOL_WORD[a.pool] ?? a.pool}</span>
          </div>
        )}
        <div className="row">
          <span className="label">Signatures</span>
          <span className="font-mono text-body tabular">
            {a.signaturesCollected} / {a.signaturesRequired}
          </span>
        </div>
        {a.approval?.required && (
          <div className="row">
            <span className="label">Approval</span>
            <span className="text-right text-body">
              {a.approval.by
                ? <>by {a.approval.by}{a.approval.at ? <span className="text-tg-hint"> · {fmtDate(a.approval.at)}</span> : null}</>
                : <span className="text-tg-hint">awaiting the second device</span>}
            </span>
          </div>
        )}
      </div>

      {/* The two-device gate (N-068): a payout is sent only with the OTHER device's word once two are registered — offered only where the card offers signing at all. */}
      {offers.approve && <ApproveOnLedger a={a} approval={approval} />}

      {unknownPayee && (
        <StateLine tone="bad" className="mt-3" more="The desk has no payable entry for this destination: it is unregistered, not approved in the Books, disabled, or one of the desk's own wallets that is never paid.">
          Not an approved payout address — do not sign.
        </StateLine>
      )}

      {left.expired ? (
        <StateLine className="mt-3" more="A lapsed card is rebuilt from a fresh block when a window is open; extending it would void any signature already collected.">
          Window lapsed — a fresh card comes with the next window.
        </StateLine>
      ) : !offers.sign ? null : onCable && cableSigns(a.kind) ? (
        // The desk's own moves by cable — and payouts too until a Stax arrives (N-089, 29 Oct 2026: "Yes, cable until
        // the Stax"; custody-screen.ts PAYOUTS_BY_CABLE). Then payouts go back to the phone only (his 22 Oct ruling).
        // One gold button per screen: the sheet's. The rows offer, in grey.
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => onCable(a)}
            className="btn-secondary flex min-h-[44px] flex-1 items-center justify-center gap-2 text-body"
          >
            <Usb className="h-3.5 w-3.5" /> Sign on Ledger
          </button>
          <button
            onClick={() => onReview(a)}
            className="btn-secondary flex min-h-[44px] items-center justify-center gap-2 text-body"
            title="Hand a signing link to the phone that holds TronLink and the Ledger"
          >
            Review on a phone <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => onReview(a)}
          className="btn-secondary mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
        >
          Review on Ledger <ExternalLink className="h-3.5 w-3.5" />
        </button>
      )}
    </Card>
  );
}

/** The second device's button on one card, and how its latest approval stands: the device's step, then the outcome. */
function ApproveOnLedger({ a, approval }: { a: ApprovalRow; approval: CardApproval | null }) {
  if (approval === null) {
    return <StateLine className="mt-3" more="The second device approves by cable: open this page in Brave or Edge on the computer it is plugged into.">Awaiting the second device.</StateLine>;
  }
  const end = approval.ended[a.txid] ?? null;
  if (end?.at === 'done') {
    return <StateLine tone="good" className="mt-3" more="The desk sends the payout once it is signed as well. The list shows who approved it on its next read.">Approved — the list refreshes.</StateLine>;
  }
  if (end?.at === 'unknown') {
    // The device signed and the answer was lost: not a failure, not a
    // success, and the attempt may still be running — only the list says,
    // and only in its own time (lib/custody-screen.ts endedStillShown).
    // No cable or device advice here: the device did its part.
    return (
      <StateLine
        tone="warn"
        className="mt-3"
        more={approvalUnknownDetail(end.error)}
        action={<button type="button" className="btn-secondary min-h-[44px] text-body lg:min-h-[40px]" onClick={approval.check}>Check again</button>}
      >
        Not confirmed — check the list before approving again.
      </StateLine>
    );
  }
  const outcome = end?.at === 'unrecorded'
    ? { line: 'Not recorded — approve again.', tone: 'bad' as const, retry: true, detail: 'The attempt\'s ten minutes have passed and the list still shows this card open: the desk did not record that approval, and cannot now.' }
    : end?.at === 'failed' ? approvalOutcome(end.error) : null;
  const mine = approval.busy === a.txid;
  return (
    <div className="mt-3 space-y-1">
      {/* words:ok — the device's own step, as ledger-cable words it */}
      {mine && approval.step && <StateLine>{approval.step}</StateLine>}
      {/* words:ok — the outcome's one line (refusals.ts approvalOutcome); what it means sits behind Details */}
      {outcome && <StateLine tone={outcome.tone} more={outcome.detail ?? undefined}>{outcome.line}</StateLine>}
      {(outcome === null || outcome.retry) && (
        <button
          onClick={() => approval.start(a)}
          disabled={approval.busy !== null}
          className="btn-secondary flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
          title="Sign the approval message on this Ledger — the other device, not the one that signs the transaction"
        >
          <Usb className="h-3.5 w-3.5" /> {mine ? 'On the Ledger…' : outcome ? 'Approve again' : 'Approve on Ledger'}
        </button>
      )}
    </div>
  );
}

export function Approvals() {
  // The signing link is minted when the operator asks for it, not when the
  // queue last refreshed — so its short window starts at intent, and no
  // credential is created for an approval nobody opened.
  const [review, setReview] = useState<ApprovalRow | null>(null);
  // The card being signed by cable, where this browser reaches a Ledger.
  const [cable, setCable] = useState<ApprovalRow | null>(null);
  const hasCable = useMemo(() => webHid() !== null, []);
  // Short interval: these carry hard expiry windows, and a stale queue is how
  // an operator walks to their device for a transaction that already lapsed.
  const q = useQuery({
    queryKey: ['custody', 'approvals'],
    queryFn: api.approvals,
    // Three seconds: the orchestrator is woken on every press (the desk's
    // NOTIFY), so a card is built within a second or two of the tap, and the
    // list must show it as fast (the operator, 21 Oct 2026: "instantly").
    refetchInterval: 3_000,
  });
  // The second device's approval, held here so the list cannot unmount it;
  // only a successful read of the list supersedes what a card shows of it.
  const approval = useCardApproval(q.data?.approvals, q.refetch);

  // A link from Deposits names its card (#card-<txid>). The browser cannot
  // reach it at load — the card exists only once the list is read — so the
  // first read brings it into view (the operator's pass, 23 Oct 2026).
  // Pay sheet round 8 (minor): once per link, when THAT card is in the list — a card built after the first read,
  // or a second link followed while already here, is reached too.
  const { hash } = useLocation();
  const target = hash.startsWith('#card-') ? hash.slice('#card-'.length) : null;
  const present = target !== null && (q.data?.approvals ?? []).some((a) => a.txid === target);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!present || scrolledFor.current === hash || typeof document === 'undefined') return;
    scrolledFor.current = hash;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [present, hash]);

  // The sheets stay mounted whatever the queue does, and so does the list
  // once it has been read: a failed 3-second poll says so above the cards it
  // last read, and unmounts nothing (Screens' Astra reviews, 22–23 Oct 2026).
  const approvals = q.data?.approvals ?? [];
  const state = listState(q);
  // One mint per card: an inline callback restarted the minting on every refresh.
  const reviewTxid = review?.txid ?? null;
  const mint = useCallback(() => api.cosignLink(reviewTxid ?? ''), [reviewTxid]);

  const failed = <ReadFailed what="the approvals" error={q.error} onRetry={() => void q.refetch()} pending={q.isFetching} />;
  const list = state.show === 'failed'
    ? failed
    : state.show === 'pending'
      ? <Pending what="Reading…" />
      : (
        <>
          {state.stale && failed}
          {approvals.length === 0
            ? <Card><EmptyState>Nothing waiting on you.</EmptyState></Card>
            : (
              <>
                {approvals.map((a) => (
                  // Addressable (#card-<txid>): Deposits links a ready card here (the operator's pass, 23 Oct 2026).
                  <div key={a.txid} id={`card-${a.txid}`}>
                    <ApprovalCard a={a} onReview={setReview} onCable={hasCable ? setCable : null} approval={hasCable ? approval : null} />
                  </div>
                ))}
                <MoreDetails label="About signing">
                  <p className="text-meta text-tg-hint">Signing happens on your Ledger{hasCable ? ' — by cable from here, or from a phone' : ''}. Check the amount and address on the device screen: it is the only display a compromised server cannot change.</p>
                </MoreDetails>
              </>
            )}
        </>
      );

  return (
    <div className="space-y-3">
      {list}

      <CableSign
        open={cable !== null}
        onClose={() => setCable(null)}
        txid={cable?.txid ?? ''}
        // Fixed words only (Astra, max, pre-live, 29 Oct 2026): the queue row's kind and amount are the server's
        // unchecked text, and beside the "type the ending" box a crafted kind could hand the operator the answer. The
        // sheet shows the card's checked figures itself.
        title={cable ? kindLabel(cable.kind) : ''}
      />

      <CosignHandoff
        open={review !== null}
        onClose={() => setReview(null)}
        title="Review on your Ledger"
        subtitle={review ? `${money(review.amount ?? '0', review.asset)} → ${review.partyName ?? 'unknown payee'}` : ''}
        mint={mint}
      />
    </div>
  );
}
